// src/app/services/plateHeatexApi.ts
import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQuery } from './apiConfig';
import {
  IIdNameValue,
  IPlateHeatex,
  IPlateHeatexParams,
  IPlateHeatexTempRating,
} from '../models';

const main_api = '/api/v1.0/master/v1.0.0/ahu/plate_heat_exchangers/mfg/';

export const plateHeatexApi = createApi({
  reducerPath: 'plateHeatexApi',
  baseQuery,
  endpoints: (builder) => ({
    getPlateHeatexParams: builder.query<
      { plate_heat_exchanger: IPlateHeatexParams },
      void
    >({
      query: () => `/api/v1.0/cms/1/v1.0.0/ahu/plate_heat_exchangers/params/`,
      keepUnusedDataFor: Infinity, // Keep the cached data until the page reloads
    }),
    getPlateHeatex: builder.query<IPlateHeatex, number>({
      query: (id) => `/api/v1.0/ahu/plate_heat_exchangers/${id}`,
    }),
    getPlateHeatexCertifications: builder.query<IIdNameValue[], number>({
      query: (id) =>
        `/api/v1.0/ahu/plate_heat_exchangers/${id}/certifications/`,
    }),
    getPlateHeatexTempRatings: builder.query<
      IPlateHeatexTempRating[],
      { mfg_id: number; series_id: number }
    >({
      query: ({ mfg_id, series_id }) =>
        `${main_api}${mfg_id}/series/${series_id}/temp_ratings/`,
      extraOptions: {
        cacheData: true,
      },
    }),
    getPlateHeatexSeals: builder.query<
      IIdNameValue[],
      { mfg_id: number; series_id: number; temp_rating_id: number }
    >({
      query: ({ mfg_id, series_id, temp_rating_id }) =>
        `${main_api}${mfg_id}/series/${series_id}/temp_rating/${temp_rating_id}/seals/`,
      extraOptions: {
        cacheData: true,
      },
    }),
    getPlateHeatexConfigurations: builder.query<
      IIdNameValue[],
      { mfg_id: number; series_id: number }
    >({
      query: ({ mfg_id, series_id }) =>
        `${main_api}${mfg_id}/series/${series_id}/configurations/`,
      extraOptions: {
        cacheData: true,
      },
    }),
    getPlateHeatexMountings: builder.query<
      IIdNameValue[],
      { mfg_id: number; series_id: number }
    >({
      query: ({ mfg_id, series_id }) =>
        `${main_api}${mfg_id}/series/${series_id}/mountings/`,
      extraOptions: {
        cacheData: true,
      },
    }),
    getPlateHeatexOrientations: builder.query<
      IIdNameValue[],
      { mfg_id: number; series_id: number }
    >({
      query: ({ mfg_id, series_id }) =>
        `${main_api}${mfg_id}/series/${series_id}/plate_orientations/`,
      extraOptions: {
        cacheData: true,
      },
    }),
    getPlateHeatexMtls: builder.query<
      IIdNameValue[],
      { mfg_id: number; series_id: number }
    >({
      query: ({ mfg_id, series_id }) =>
        `${main_api}${mfg_id}/series/${series_id}/plate_mtls/`,
      extraOptions: {
        cacheData: true,
      },
    }),
    getPlateHeatexFinishs: builder.query<
      IIdNameValue[],
      { mfg_id: number; series_id: number; plate_mtl_id: number }
    >({
      query: ({ mfg_id, series_id, plate_mtl_id }) =>
        `${main_api}${mfg_id}/series/${series_id}/plate_mtl/${plate_mtl_id}/plate_finishs/`,
      extraOptions: {
        cacheData: true,
      },
    }),
    getPlateHeatexEndPlateMtls: builder.query<
      IIdNameValue[],
      { mfg_id: number; series_id: number }
    >({
      query: ({ mfg_id, series_id }) =>
        `${main_api}${mfg_id}/series/${series_id}/end_plate_mtls/`,
      extraOptions: {
        cacheData: true,
      },
    }),
    getPlateHeatexEndPlateFinishs: builder.query<
      IIdNameValue[],
      { mfg_id: number; series_id: number; end_plate_mtl_id: number }
    >({
      query: ({ mfg_id, series_id, end_plate_mtl_id }) =>
        `${main_api}${mfg_id}/series/${series_id}/end_plate_mtl/${end_plate_mtl_id}/end_plate_finishs/`,
      extraOptions: {
        cacheData: true,
      },
    }),
    getPlateHeatexEndPlateStyles: builder.query<
      IIdNameValue[],
      { mfg_id: number; series_id: number; size_id: number }
    >({
      query: ({ mfg_id, series_id, size_id }) =>
        `${main_api}${mfg_id}/series/${series_id}/size/${size_id}/end_plate_styles/`,
      extraOptions: {
        cacheData: true,
      },
    }),
    getPlateHeatexCornerProfileStyles: builder.query<
      IIdNameValue[],
      { mfg_id: number; series_id: number }
    >({
      query: ({ mfg_id, series_id }) =>
        `${main_api}${mfg_id}/series/${series_id}/corner_profile_styles/`,
      extraOptions: {
        cacheData: true,
      },
    }),
    getPlateHeatexCornerProfileMtls: builder.query<
      IIdNameValue[],
      { mfg_id: number; series_id: number }
    >({
      query: ({ mfg_id, series_id }) =>
        `${main_api}${mfg_id}/series/${series_id}/corner_profile_mtls/`,
      extraOptions: {
        cacheData: true,
      },
    }),
    getPlateHeatexCornerProfileFinishs: builder.query<
      IIdNameValue[],
      { mfg_id: number; series_id: number; corner_profile_mtl_id: number }
    >({
      query: ({ mfg_id, series_id, corner_profile_mtl_id }) =>
        `${main_api}${mfg_id}/series/${series_id}/corner_profile_mtl/${corner_profile_mtl_id}/corner_profile_finishs/`,
      extraOptions: {
        cacheData: true,
      },
    }),
    getPlateHeatexBypassDmrMtls: builder.query<
      IIdNameValue[],
      { mfg_id: number; series_id: number }
    >({
      query: ({ mfg_id, series_id }) =>
        `${main_api}${mfg_id}/series/${series_id}/bypass_dmr_mtls/`,
      extraOptions: {
        cacheData: true,
      },
    }),
    getPlateHeatexBypassDmrFins: builder.query<
      IIdNameValue[],
      { mfg_id: number; series_id: number; bypass_dmr_mtl_id: number }
    >({
      query: ({ mfg_id, series_id, bypass_dmr_mtl_id }) =>
        `${main_api}${mfg_id}/series/${series_id}/bypass_dmr_mtl/${bypass_dmr_mtl_id}/bypass_dmr_fins/`,
      extraOptions: {
        cacheData: true,
      },
    }),
  }),
});

export const {
  useLazyGetPlateHeatexQuery,
  useLazyGetPlateHeatexCertificationsQuery,
  useGetPlateHeatexParamsQuery,
  useLazyGetPlateHeatexTempRatingsQuery,
  useLazyGetPlateHeatexSealsQuery,
  useLazyGetPlateHeatexConfigurationsQuery,
  useLazyGetPlateHeatexMountingsQuery,
  useLazyGetPlateHeatexOrientationsQuery,
  useLazyGetPlateHeatexMtlsQuery,
  useLazyGetPlateHeatexFinishsQuery,
  useLazyGetPlateHeatexEndPlateMtlsQuery,
  useLazyGetPlateHeatexEndPlateFinishsQuery,
  useLazyGetPlateHeatexEndPlateStylesQuery,
  useLazyGetPlateHeatexCornerProfileFinishsQuery,
  useLazyGetPlateHeatexCornerProfileMtlsQuery,
  useLazyGetPlateHeatexCornerProfileStylesQuery,
  useLazyGetPlateHeatexBypassDmrFinsQuery,
  useLazyGetPlateHeatexBypassDmrMtlsQuery
} = plateHeatexApi;
